import { useState, useEffect, useRef } from 'react';

// A small, safe wrapper around sessionStorage with React state syncing
export default function useSessionStorage(key, initialValue) {
  const isMounted = useRef(false);

  const readValue = () => {
    if (typeof window === 'undefined') return initialValue;
    try {
      const item = window.sessionStorage.getItem(key);
      return item !== null ? JSON.parse(item) : initialValue;
    } catch (err) {
      // If JSON parsing fails or storage is not available, fallback
      return initialValue;
    }
  };

  const [storedValue, setStoredValue] = useState(readValue);

  // Update sessionStorage when value changes
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    try {
      window.sessionStorage.setItem(key, JSON.stringify(storedValue));
    } catch (err) {
      // ignore write errors (quota, privacy modes)
    }
  }, [key, storedValue]);

  // Sync across tabs/windows in the same session (rare for sessionStorage, but safe)
  useEffect(() => {
    const onStorage = (event) => {
      if (event.storageArea === window.sessionStorage && event.key === key) {
        try {
          setStoredValue(event.newValue ? JSON.parse(event.newValue) : initialValue);
        } catch {
          setStoredValue(initialValue);
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [key, initialValue]);

  return [storedValue, setStoredValue];
}
